import React, { useEffect, useRef, useState } from 'react';
import { animateScroll } from 'react-scroll';
import styled, { css } from 'styled-components';

import {
  BUTTON_THEMES,
  BUTTON_TYPES,
  CARD_WRAPPER_TYPES,
  ELEMENT_ACTION,
  ICON_TYPES,
  InputType,
  NOTIFICATION_TYPES,
} from '../../../shared/constants';
import { PageType } from '../../../shared/constants/Contentful.constants';
import {
  IContactForm,
  IContentfulLink,
  IFormValues,
  IParentPage,
} from '../../../types/shared';

import FormMessage, { FORM_TYPES } from '../../atoms/form-message/FormMessage';
import SecondaryButton from '../../atoms/secondary-button/SecondaryButton';

import {
  DEQ_COMPS,
  DEQ_ERROR_TYPES,
  DEQ_PAGE_TYPES,
  DEQ_TYPES,
  trackFormStepEvent,
} from '../../../utils/deq';
import { FORM_TRACKING_STATUS } from '../../../utils/deq/formTrackPageView';
import { trackInputErrors } from '../../../utils/deq/trackInputErrors';
import { copySelectorHelper } from '../../../utils/deq/trackInputErrors.helpers';
import { StyledPrimaryButton } from '../../../utils/forms/buttonStyles';
import { FORM_STATES } from '../../../utils/forms/constants';
import { renderFormInput } from '../../../utils/forms/renderFormInput';
import { getInternalLink } from '../../../utils/generic';
import ReactMarkdownExtended from '../../../utils/generic/ReactMarkdownExtended';
import {
  LABELS,
  languageHelper,
  TRACKING_LABELS,
} from '../../../utils/language';
import { setMessageBasedOnCode } from '../../../utils/string-utils';

import mq from '../../../styles/breakpoints';
import Colors from '../../../styles/colors';
import { Body, H2, IntroText, SmallTextCss } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';

import { contactFormData } from '../../../data/static-content/forms/contact-form/static-content';
import {
  createGraphMailTemplate,
  handleReCaptcha,
} from '../../../services/re-captcha.service';
import FormStatusCard from '../../molecules/form-status-card/FormStatusCard';
import Form from '../form/Form';

interface Props extends IContactForm {
  productName: string;
  pageType: PageType;
  cardType: string;
}

const LeadForm = ({
  pageType = PageType.CONTACT_FORM,
  cardType,
  id,
  title,
  rawSubtitle,
  description,
  primaryLink,
  secondaryLink,
  formInputs,
  email,
  showPrivacyStatement,
  productName,
  successTitle,
  successDescription,
  successPrimaryLink,
  successSecondaryLink,
  trackingName,
}: Props) => {
  const [formState, setFormState] = useState(FORM_STATES.default);
  const [isNotificationOpen, setIsNotificationOpen] = useState(true);
  const [toggleClick, setToggleClick] = useState(false);
  const { lang } = languageHelper();
  const formRef = useRef<HTMLFormElement>(null);

  const deqFormName = `gated-content-form ${productName}`;

  const scrollOptions = {
    duration: 500,
    smooth: true,
  };
  const subtitle =
    formState === FORM_STATES.success
      ? successTitle
      : rawSubtitle?.childMarkdownRemark.rawMarkdownBody;

  const handleSubmit = async (formResult: IFormValues) => {
    setFormState(FORM_STATES.loading);
    if (pageType === PageType.GATED_CONTACT)
      animateScroll.scrollToTop(scrollOptions);
    const emailTemplate = createGraphMailTemplate(
      formResult,
      email.emailAddresses,
      email.title,
      email.body
    );

    handleReCaptcha(emailTemplate, callback, errCallback);
  };

  const callback = (response: { message: string }) => {
    if (response.message === 'Sent e-mail') {
      setFormState(FORM_STATES.success);
      trackFormStepEvent(
        pageType,
        id,
        FORM_TRACKING_STATUS.endSuccess,
        deqFormName,
        '',
        'lead-soft'
      );
      if (primaryLink?.asset?.file.url || primaryLink?.externalUrl) {
        window.open(
          primaryLink.asset?.file.url || (primaryLink.externalUrl as string),
          '_blank'
        );
      }
    }
    return response;
  };

  const errCallback = (response: {
    statusCode?: number;
    statusText?: string;
    url?: string;
    error?: string;
    message?: string;
    name?: string;
  }) => {
    const { statusCode, statusText, url, error, name, message } = response;
    const isTypeError = response instanceof TypeError;

    setFormState(FORM_STATES.error);
    trackFormStepEvent(
      pageType,
      id,
      FORM_TRACKING_STATUS.endFail,
      deqFormName,
      undefined,
      undefined,
      {
        type: DEQ_ERROR_TYPES.technical,
        code: isTypeError ? name : statusCode,
        source: url,
        message: isTypeError
          ? message
          : statusText ||
            error ||
            setMessageBasedOnCode(statusCode) ||
            TRACKING_LABELS.errorMessage[lang],
      }
    );
  };

  const renderFormInputs = () => (
    <InputWrapper>
      <InputContainer layout={pageType}>
        {formInputs
          .filter((input) => input.checkboxType !== InputType.LEGAL_CHECKBOX)
          .map((item, key) => renderFormInput(item, key))}
      </InputContainer>
      {formInputs
        .filter((input) => input.checkboxType === InputType.LEGAL_CHECKBOX)
        .map((item, key) => renderFormInput(item, key))}
    </InputWrapper>
  );

  useEffect(() => {
    if (formState !== FORM_STATES.success && formState !== FORM_STATES.loading)
      if (formRef) {
        const requiredCopy = copySelectorHelper(
          formRef,
          '[data-testid="notification-text"]',
          lang
        );

        trackInputErrors(DEQ_TYPES.formInput, requiredCopy);
      }
  }, [toggleClick]);

  const onClickSubmitHandler = () => {
    setToggleClick(!toggleClick);
  };

  return (
    <div>
      <FormContent>
        {(title || subtitle || description) && (
          <DefaultHeader data-testid="default-header">
            {title && <H2>{title}</H2>}
            {cardType === CARD_WRAPPER_TYPES.gatedContentWrapper ? (
              <Body>
                <ReactMarkdownExtended componentType={DEQ_COMPS.leadForm}>
                  {subtitle ||
                    rawSubtitle?.childMarkdownRemark?.rawMarkdownBody}
                </ReactMarkdownExtended>
              </Body>
            ) : (
              <StyledIntroText>
                <ReactMarkdownExtended componentType={DEQ_COMPS.leadForm}>
                  {subtitle ||
                    rawSubtitle?.childMarkdownRemark?.rawMarkdownBody}
                </ReactMarkdownExtended>
              </StyledIntroText>
            )}
            {formState !== FORM_STATES.success && (
              <Body>
                <ReactMarkdownExtended componentType={DEQ_COMPS.leadForm}>
                  {description?.childMarkdownRemark.rawMarkdownBody}
                </ReactMarkdownExtended>
              </Body>
            )}
          </DefaultHeader>
        )}
        {formState === FORM_STATES.success && (
          <SuccessContainer data-testid="success-container">
            <Body>
              <ReactMarkdownExtended componentType={DEQ_COMPS.leadForm}>
                {successDescription?.childMarkdownRemark.rawMarkdownBody}
              </ReactMarkdownExtended>
            </Body>
            <SuccessButtons
              primaryLink={primaryLink}
              secondaryLink={secondaryLink}
              successPrimaryLink={successPrimaryLink}
              successSecondaryLink={successSecondaryLink}
            />
          </SuccessContainer>
        )}
        {formState !== FORM_STATES.success && (
          <div>
            {showPrivacyStatement &&
              cardType === CARD_WRAPPER_TYPES.gatedContentWrapper && (
                <StyledFormMessage
                  type={FORM_TYPES.info}
                  isNotificationOpen={isNotificationOpen}
                  onNotificationClick={(event) => setIsNotificationOpen(event)}
                >
                  <ReactMarkdownExtended componentType={DEQ_COMPS.leadForm}>
                    {
                      contactFormData.infoMessage.privacyPolicyAndReCaptcha[
                        lang
                      ]
                    }
                  </ReactMarkdownExtended>
                </StyledFormMessage>
              )}
            <Form
              id={id}
              trackPageView={
                cardType !== CARD_WRAPPER_TYPES.gatedContentWrapper
              }
              submit={handleSubmit}
              stepName={trackingName || title}
              deqFormName={deqFormName}
              pageType={pageType}
              ref={formRef}
            >
              <FormStatusCard
                title={
                  contactFormData.userFeedback.errorGatedContent.title[lang]
                }
                statusType={NOTIFICATION_TYPES.error}
                show={formState === FORM_STATES.error}
                customPrimaryClick={() => setFormState(FORM_STATES.default)}
                hasShadow={false}
              >
                <ReactMarkdownExtended componentType={DEQ_COMPS.leadForm}>
                  {
                    contactFormData.userFeedback.errorGatedContent.description[
                      lang
                    ]
                  }
                </ReactMarkdownExtended>
              </FormStatusCard>
              <FormStatusCard
                statusType={NOTIFICATION_TYPES.loading}
                show={formState === FORM_STATES.loading}
                hasShadow={false}
              >
                <ReactMarkdownExtended componentType={DEQ_COMPS.leadForm}>
                  {contactFormData.userFeedback.loading.email[lang]}
                </ReactMarkdownExtended>
              </FormStatusCard>
              {formState === FORM_STATES.default && (
                <>
                  {renderFormInputs()}
                  {showPrivacyStatement &&
                    cardType === CARD_WRAPPER_TYPES.formWrapper && (
                      <StyledBody>
                        <ReactMarkdownExtended
                          componentType={DEQ_COMPS.leadForm}
                        >
                          {
                            contactFormData.infoMessage
                              .privacyPolicyAndReCaptcha[lang]
                          }
                        </ReactMarkdownExtended>
                      </StyledBody>
                    )}
                  {showPrivacyStatement &&
                    pageType === PageType.GATED_CONTACT && (
                      <StyledReactMarkdownExtended
                        componentType={DEQ_COMPS.leadForm}
                      >
                        {
                          contactFormData.infoMessage.privacyPolicyAndReCaptcha[
                            lang
                          ]
                        }
                      </StyledReactMarkdownExtended>
                    )}
                  <FormBtnWrapper>
                    <EditStyledPrimaryButton
                      type={BUTTON_TYPES.submit}
                      data-testid="submit-button"
                      buttonAction={BUTTON_TYPES.submit}
                      label={primaryLink?.label || LABELS.submit[lang]}
                      icon={ICON_TYPES.file}
                      buttonTheme={BUTTON_THEMES.highlight}
                      customOnClick={() =>
                        setTimeout(() => {
                          onClickSubmitHandler();
                        }, 0)
                      }
                      deqComponentTitle={`${LABELS.submit[lang]}-form`}
                      deqCustomAttributes={{
                        componentType: DEQ_TYPES.contactForm,
                        elementTarget: '',
                        elementAction: '',
                      }}
                    />
                    {secondaryLink && (
                      <SecondaryButton
                        buttonTheme={BUTTON_THEMES.default}
                        to={
                          secondaryLink.linkedPage
                            ? `/${getInternalLink(
                                secondaryLink.linkedPage as IParentPage
                              )}`
                            : secondaryLink.externalUrl
                        }
                        className="link"
                        deqComponentTitle={secondaryLink.label}
                        label={secondaryLink.label}
                        icon={ICON_TYPES.default}
                        type={secondaryLink.type}
                        iconPosition={secondaryLink.iconPosition}
                        deqCustomAttributes={{
                          componentType: DEQ_TYPES.contactForm,
                          elementType: DEQ_TYPES.textLink,
                          elementTarget: secondaryLink.linkedPage
                            ? `/${getInternalLink(
                                secondaryLink.linkedPage as IParentPage
                              )}`
                            : secondaryLink.externalUrl,
                          elementAction: ELEMENT_ACTION.close,
                        }}
                      />
                    )}
                  </FormBtnWrapper>
                </>
              )}
            </Form>
          </div>
        )}
      </FormContent>
    </div>
  );
};

interface SuccesButtonsProps {
  primaryLink?: IContentfulLink;
  secondaryLink?: IContentfulLink;
  successPrimaryLink?: IContentfulLink;
  successSecondaryLink?: IContentfulLink;
}

const SuccessButtons = ({
  primaryLink,
  secondaryLink,
  successPrimaryLink,
  successSecondaryLink,
}: SuccesButtonsProps) => {
  const { lang } = languageHelper();

  return (
    <SuccessButtonWrapper>
      {successPrimaryLink ? (
        <EditStyledPrimaryButton
          type={successPrimaryLink.type}
          label={successPrimaryLink.label}
          to={
            successPrimaryLink.linkedPage
              ? `/${getInternalLink(
                  successPrimaryLink.linkedPage as IParentPage
                )}`
              : successPrimaryLink.externalUrl
          }
          data-testid="success-primary-link"
          icon={successPrimaryLink.icon}
          buttonTheme={successPrimaryLink.theme}
          deqComponentTitle={`${successPrimaryLink.label}-form`}
          deqCustomAttributes={{
            componentType: DEQ_TYPES.contactForm,
            elementTarget:
              successPrimaryLink.linkedPage?.slug ||
              successPrimaryLink.externalUrl,
          }}
        />
      ) : primaryLink ? (
        <EditStyledPrimaryButton
          type={BUTTON_TYPES.primary}
          label={contactFormData.userFeedback.success.downloadAgain[lang]}
          to={
            primaryLink.asset?.file.url || (primaryLink.externalUrl as string)
          }
          target="_blank"
          data-testid="submit-button"
          icon={ICON_TYPES.file}
          buttonTheme={BUTTON_THEMES.highlight}
          deqComponentTitle={`${primaryLink.label}-form`}
          deqCustomAttributes={{
            componentType: DEQ_TYPES.contactForm,
            elementTarget:
              primaryLink.asset?.file.url ||
              (primaryLink.externalUrl as string),
          }}
        />
      ) : (
        <EditStyledPrimaryButton
          type={BUTTON_TYPES.submit}
          data-testid="submit-button"
          buttonAction={BUTTON_TYPES.submit}
          label={LABELS.submit[lang]}
          icon={ICON_TYPES.none}
          buttonTheme={BUTTON_THEMES.highlight}
          deqComponentTitle="Indienen-form"
          deqCustomAttributes={{
            componentType: 'contact-form',
            elementTarget: '',
            elementAction: BUTTON_TYPES.submit,
          }}
        />
      )}
      {successSecondaryLink ? (
        <SecondaryButton
          buttonTheme={BUTTON_THEMES.default}
          to={
            successSecondaryLink.linkedPage
              ? `/${getInternalLink(
                  successSecondaryLink.linkedPage as IParentPage
                )}`
              : successSecondaryLink.externalUrl
          }
          className="link"
          data-testid="success-secondary-link"
          deqComponentTitle={successSecondaryLink.label}
          label={successSecondaryLink.label}
          icon={successSecondaryLink.icon || ICON_TYPES.default}
          type={successSecondaryLink.type}
          iconPosition={successSecondaryLink.iconPosition}
          deqCustomAttributes={{
            componentType: DEQ_TYPES.contactForm,
            elementTarget: successSecondaryLink.linkedPage
              ? `/${getInternalLink(
                  successSecondaryLink.linkedPage as IParentPage
                )}`
              : successSecondaryLink.externalUrl,
            elementAction: ELEMENT_ACTION.exit,
          }}
        />
      ) : (
        secondaryLink && (
          <SecondaryButton
            buttonTheme={BUTTON_THEMES.default}
            to={
              secondaryLink.linkedPage
                ? `/${getInternalLink(secondaryLink.linkedPage as IParentPage)}`
                : secondaryLink.externalUrl
            }
            className="link"
            deqComponentTitle={secondaryLink.label}
            label={secondaryLink.label}
            icon={ICON_TYPES.default}
            type={secondaryLink.type}
            iconPosition={secondaryLink.iconPosition}
            deqCustomAttributes={{
              componentType: DEQ_TYPES.contactForm,
              elementTarget: secondaryLink.linkedPage
                ? `/${getInternalLink(secondaryLink.linkedPage as IParentPage)}`
                : secondaryLink.externalUrl,
              elementAction: ELEMENT_ACTION.close,
            }}
          />
        )
      )}
    </SuccessButtonWrapper>
  );
};

export default LeadForm;

const DefaultHeader = styled.div`
  margin-bottom: ${Spacings.S};

  & > h2 {
    margin-bottom: ${Spacings.XS};
    margin-top: 0;

    p {
      margin-bottom: ${Spacings.XS};
      margin-top: 0;
    }
  }

  ${mq.from.breakpoint.M`
    margin-bottom: ${Spacings.M};
  `}
`;

const FormContent = styled.div`
  background-color: ${Colors.white};
  padding: ${Spacings.S};

  ${mq.from.breakpoint.M`
    padding: ${Spacings.L};
  `}

  ${mq.from.breakpoint.L`
    padding: ${Spacings.M};
  `}

  ${mq.from.breakpoint.XL`
    padding: ${Spacings.L};
  `}
`;

const InputContainer = styled.div<{ layout?: string }>`
  display: block;

  & > * {
    margin-bottom: ${Spacings.M};
  }

  & > *:last-child {
    margin-bottom: 0;
  }

  ${mq.from.breakpoint.M`
    display: grid;
    grid-column-gap: ${Spacings.M};
    grid-row-gap: ${Spacings.M};

    & > * {
      margin-bottom: 0;
    }

    ${({ layout }: { layout?: string }) =>
      layout === PageType.GATED_CONTACT
        ? css`
            grid-template-columns: repeat(1, 1fr);

            input {
              max-width: inherit;
            }
          `
        : layout === PageType.PRODUCT
        ? css`
            grid-template-columns: repeat(2, 2fr);
          `
        : undefined}
  `}
`;

const StyledFormMessage = styled(FormMessage)`
  margin-bottom: ${Spacings.S};
`;

const StyledBody = styled(Body)`
  color: ${Colors.abnNeutral500};
  font-size: 14px;
  line-height: 20px;
  margin-bottom: ${Spacings.M};
`;

const EditStyledPrimaryButton = styled(StyledPrimaryButton)`
  margin-bottom: 0;
  margin-right: 0;
`;

const InputWrapper = styled.div`
  & > * {
    margin-bottom: ${Spacings.S};

    ${mq.from.breakpoint.L`
      margin-bottom: ${Spacings.M};
    `}
  }
`;

const SuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const SuccessButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${Spacings.M};
  margin-top: ${Spacings.L};
`;

const FormBtnWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${Spacings.M};

  ${mq.to.breakpoint.M`
    display: flex;
    flex-wrap: wrap;
    row-gap: ${Spacings.M};

    > button {
      width: 100%;
    }
  `}
`;

const StyledIntroText = styled(IntroText)`
  > p {
    margin-bottom: ${Spacings.XS};
    margin-top: 0;
  }
`;

const StyledReactMarkdownExtended = styled(ReactMarkdownExtended)`
  ${SmallTextCss};
  color: ${Colors.abnNeutral500};
  margin-bottom: ${Spacings.S};

  p {
    margin: 0;
  }

  ${mq.from.breakpoint.L`
    margin-bottom: ${Spacings.M};
  `}
`;
